"use client";

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetAtsConnection,
  usePatchAtsSyncSettings,
  getGetAtsConnectionQueryKey,
} from "@/lib/api/generated";
import type { SyncSettingsPatch } from "@/lib/api/model";
import { showToast } from "@/components/ui/sonner";

/** The subset of the (untyped) connection `sync_settings` dict we read. */
interface SyncSettingsShape {
  auto_sync_on_add?: unknown;
  auto_enrich_on_auto_sync?: unknown;
  show_job_ids_in_picker?: unknown;
}

/** Cache shape we mutate for the optimistic toggle. The connection GET response
 *  carries an untyped `sync_settings` dict, so keep this deliberately loose. */
type ConnectionCache = { sync_settings?: Record<string, unknown> } & Record<
  string,
  unknown
>;

/** View-model field names <-> API keys. `set()` speaks the view-model names. */
export type SyncSettingField =
  | "autoSyncOnAdd"
  | "autoEnrichOnAutoSync"
  | "showJobIdsInPicker";

const FIELD_TO_API: Record<SyncSettingField, keyof SyncSettingsPatch> = {
  autoSyncOnAdd: "auto_sync_on_add",
  autoEnrichOnAutoSync: "auto_enrich_on_auto_sync",
  showJobIdsInPicker: "show_job_ids_in_picker",
};

export interface ConnectionSyncSettings {
  autoSyncOnAdd: boolean;
  autoEnrichOnAutoSync: boolean;
  showJobIdsInPicker: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Reads a connection's `sync_settings` and narrows the untyped dict. Query is
 * disabled (and every flag false) until a `connectionId` is known. This is the
 * single read surface for connection sync settings — `useJobAtsLink`'s
 * `useConnectionSyncSettings` re-exports it so the add-to-job call site keeps
 * working.
 */
export function useConnectionSyncSettings(
  connectionId: string | null,
): ConnectionSyncSettings {
  const query = useGetAtsConnection(connectionId ?? "", {
    query: { enabled: !!connectionId },
  });
  const settings = query.data?.sync_settings as SyncSettingsShape | undefined;
  return {
    autoSyncOnAdd: settings?.auto_sync_on_add === true,
    autoEnrichOnAutoSync: settings?.auto_enrich_on_auto_sync === true,
    showJobIdsInPicker: settings?.show_job_ids_in_picker === true,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export interface UseSyncSettings extends ConnectionSyncSettings {
  /** Toggle one field: partial PATCH, optimistic, rollback + toast on error. */
  set: (field: SyncSettingField, value: boolean) => void;
  isPending: boolean;
}

/**
 * Query + mutation surface for a connection's auto-sync settings. `set()` sends
 * a partial PATCH for the single toggled field, updates the connection query
 * cache optimistically, rolls back + toasts on failure, and invalidates the
 * connection query on success (so it reflects the server-resolved value).
 */
export function useSyncSettings(connectionId: string | null): UseSyncSettings {
  const qc = useQueryClient();
  const read = useConnectionSyncSettings(connectionId);
  const mutation = usePatchAtsSyncSettings();

  const set = useCallback(
    (field: SyncSettingField, value: boolean) => {
      if (!connectionId) return;

      const apiField = FIELD_TO_API[field];
      const queryKey = getGetAtsConnectionQueryKey(connectionId);
      const previous = qc.getQueryData<ConnectionCache>(queryKey);

      // Optimistic write so the switch reflects the new state immediately.
      qc.setQueryData<ConnectionCache>(queryKey, (old) =>
        old
          ? {
              ...old,
              sync_settings: { ...(old.sync_settings ?? {}), [apiField]: value },
            }
          : old,
      );

      mutation.mutate(
        { connectionId, data: { [apiField]: value } },
        {
          onError: (err) => {
            // Restore the pre-toggle snapshot and tell the user it didn't stick.
            qc.setQueryData(queryKey, previous);
            showToast.error("Couldn't update sync settings", {
              description:
                err instanceof Error ? err.message : "Please try again.",
            });
          },
          onSuccess: () => {
            void qc.invalidateQueries({ queryKey });
          },
        },
      );
    },
    [connectionId, qc, mutation],
  );

  return { ...read, set, isPending: mutation.isPending };
}
